import Link from 'next/link';
import { ReactNode } from 'react';
import styled from 'styled-components';

import theme from '../../assets/theme';
import {
  blogCategoryPath,
  BlogPost,
  blogPostPath,
  formatBlogDate,
  getBlogCategory,
} from '../../content/blog/posts';
import { getCanonicalUrl } from '../../utils/client';
import MaxWidthContainer from '../_atoms/MaxWidthContainer';
import LandingPageLayout from '../layouts/LandingPageLayout';

const MOBILE = '(max-width: 992px)';
const NARROW = '(max-width: 360px)';

type Props = {
  post: BlogPost;
  children: ReactNode;
};

const BlogArticle = ({ post, children }: Props) => {
  const category = getBlogCategory(post.category);
  if (!category) {
    throw new Error(`Unknown blog category: ${post.category}`);
  }

  const path = blogPostPath(post.slug);
  const canonical = getCanonicalUrl(path);
  const image = getCanonicalUrl(post.cover);
  const dateLabel = formatBlogDate(post.publishedAt);

  const structuredData: Record<string, unknown>[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.description,
      datePublished: post.publishedAt,
      image,
      author: {
        '@type': 'Organization',
        name: post.author,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Curipod',
        url: 'https://curipod.com',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonical,
      },
      url: canonical,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: getCanonicalUrl('/'),
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: getCanonicalUrl('/c/blogs'),
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: category.name,
          item: getCanonicalUrl(blogCategoryPath(category.slug)),
        },
        {
          '@type': 'ListItem',
          position: 4,
          name: post.title,
          item: canonical,
        },
      ],
    },
  ];

  return (
    <LandingPageLayout
      title={post.seoTitle}
      subTitle={post.description}
      disableHeadline
      ogType="article"
      canonicalPath={path}
      imageUrl={image}
      structuredData={structuredData}
    >
      <Hero>
        <MaxWidthContainer>
          <HeroInner>
            <Breadcrumb>
              <Link href="/" passHref>
                <CrumbLink>Home</CrumbLink>
              </Link>
              <CrumbSep aria-hidden="true">/</CrumbSep>
              <Link href="/c/blogs" passHref>
                <CrumbLink>Blog</CrumbLink>
              </Link>
              <CrumbSep aria-hidden="true">/</CrumbSep>
              <Link href={blogCategoryPath(category.slug)} passHref>
                <CrumbLink>{category.name}</CrumbLink>
              </Link>
            </Breadcrumb>
            <HeroTitle>{post.title}</HeroTitle>
            <Byline>
              By {post.author}
              <BylineSep aria-hidden="true"> | </BylineSep>
              <time dateTime={post.publishedAt}>{dateLabel}</time>
            </Byline>
          </HeroInner>
        </MaxWidthContainer>
      </Hero>
      <Body>
        <MaxWidthContainer>
          <Column>
            <Cover src={post.cover} alt={post.coverAlt} />
            <Prose>{children}</Prose>
          </Column>
        </MaxWidthContainer>
      </Body>
    </LandingPageLayout>
  );
};

export default BlogArticle;

const Hero = styled.header`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    pointer-events: none;
  }

  &::before {
    width: 520px;
    height: 520px;
    top: -180px;
    right: -80px;
  }

  &::after {
    width: 320px;
    height: 320px;
    bottom: -120px;
    left: -80px;
  }
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  padding: 56px 0 48px;
  max-width: 760px;

  @media ${MOBILE} {
    padding: 40px 0 32px;
  }

  @media ${NARROW} {
    padding: 32px 0 28px;
  }
`;

const Breadcrumb = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 18px;
  font-size: 14px;
  line-height: 1.4;
`;

const CrumbLink = styled.a`
  color: ${theme.colors.white};
  text-decoration: underline;
  text-underline-offset: 2px;
  font-weight: 600;

  &:hover {
    color: ${theme.colors.white};
  }
`;

const CrumbSep = styled.span`
  color: rgba(255, 255, 255, 0.75);
`;

const HeroTitle = styled.h1`
  margin: 0 0 16px;
  color: ${theme.colors.white};
  font-size: 44px;
  line-height: 1.15;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
  overflow-wrap: anywhere;

  @media ${MOBILE} {
    font-size: 32px;
  }

  @media ${NARROW} {
    font-size: 26px;
  }
`;

const Byline = styled.p`
  margin: 0;
  color: ${theme.colors.white};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;

  @media ${NARROW} {
    font-size: 15px;
  }
`;

const BylineSep = styled.span`
  color: rgba(255, 255, 255, 0.8);
`;

const Body = styled.div`
  background: ${theme.colors.background};
  width: 100%;
`;

const Column = styled.article`
  max-width: 760px;
  margin: 0 auto;
  padding: 32px 0 80px;

  @media ${MOBILE} {
    padding-bottom: 56px;
  }
`;

const Cover = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 10px;
  background: ${theme.colors.white};
`;

const Prose = styled.div`
  margin-top: 28px;
  color: ${theme.colors.black};
  overflow-wrap: break-word;

  h2,
  h3,
  h4,
  h5 {
    line-height: 1.25;
    margin: 36px 0 12px;
    overflow-wrap: anywhere;
  }

  h2 {
    font-size: 32px;
  }

  h3 {
    font-size: 24px;
  }

  h4,
  h5 {
    font-size: 20px;
  }

  p,
  li {
    font-size: 18px;
    line-height: 1.7;
    font-weight: 450;
  }

  p {
    margin: 0 0 18px;
  }

  ul,
  ol {
    margin: 0 0 18px;
    padding-left: 1.3em;
  }

  li {
    margin-bottom: 8px;
  }

  a {
    color: ${theme.colors.black};
    font-weight: 650;
    text-decoration: underline;
    text-decoration-color: ${theme.colors.orange};
    text-underline-offset: 3px;
    word-break: break-word;
  }

  a:hover {
    color: ${theme.colors.orangeDark};
  }

  strong {
    font-weight: 700;
  }

  blockquote {
    margin: 28px 0;
    padding: 4px 0 4px 18px;
    border-left: 4px solid ${theme.colors.orange};
  }

  blockquote p {
    font-size: 20px;
  }

  figure {
    margin: 28px 0;
  }

  figcaption {
    margin-top: 10px;
    font-size: 15px;
    font-style: italic;
    line-height: 1.5;
  }

  img {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 10px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 0 0 12px;
    font-size: 16px;
  }

  th,
  td {
    border-bottom: 1px solid ${theme.colors.beigeDark};
    padding: 10px 12px;
    text-align: left;
    vertical-align: top;
    overflow-wrap: anywhere;
  }

  th {
    background: ${theme.colors.lightOrangeBg};
    font-weight: 700;
  }

  @media ${MOBILE} {
    h2 {
      font-size: 26px;
    }

    h3 {
      font-size: 22px;
    }

    p,
    li,
    a {
      font-size: 17px;
    }
  }

  @media ${NARROW} {
    h2 {
      font-size: 22px;
    }

    h3 {
      font-size: 19px;
    }

    p,
    li,
    a,
    blockquote p {
      font-size: 16px;
    }

    table {
      font-size: 14px;
    }

    th,
    td {
      padding: 8px;
    }

    figcaption {
      font-size: 14px;
    }
  }
`;
